import type { UserStateSavedSearchFolderKey, UserStateSavedSearchKey } from "./user-state";

export const EMAIL_ALERTS_ITEM_KEY = "emailAlerts" as const;
export const SAVED_SEARCH_ITEM_KEY_PREFIX = "savedSearch#" as const;
export const SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX = "savedSearchFolder#" as const;

/**
 * Identifier of the consent copy version shown at opt-in (spec §9). Bump when the copy
 * changes. v2: the copy no longer promises "no other emails, ever" — a signed-up user can
 * now separately opt into a second, independently-scheduled watchlist digest (see
 * WatchlistDigestAlertsData below); the toggle enabling it is its own consent act, but the
 * blanket "no other emails" claim in the base copy would otherwise be inaccurate for anyone
 * who turns it on.
 */
export const ALERTS_CONSENT_TEXT_VERSION = "alerts-consent-v2";

export const ALERT_FREQUENCIES = ["weekly", "daily"] as const;
export type AlertFrequency = (typeof ALERT_FREQUENCIES)[number];
export const DEFAULT_ALERT_FREQUENCY: AlertFrequency = "weekly";

export const EMAIL_ALERTS_STATUSES = ["active", "pendingConfirmation", "unsubscribed"] as const;
export type EmailAlertsStatus = (typeof EMAIL_ALERTS_STATUSES)[number];

export type AlertWithdrawalChannel = "profile" | "emailLink" | "oneClick" | "bounce" | "complaint";

export interface EmailAlertsData extends Record<string, unknown> {
  frequency: AlertFrequency;
  /** Captured from the session at opt-in; cleared on unsubscribe (spec §9). */
  email?: string;
  emailVerified: boolean;
  status: EmailAlertsStatus;
  locale: "en" | "fi" | "sv";
  /** Epoch ms of the last successful digest send; window lower bound. */
  lastSentAt: number | null;
  consentAt: number;
  consentTextVersion: string;
  consentSource: string;
  withdrawnAt?: number;
  withdrawnVia?: AlertWithdrawalChannel;
}

export interface SavedSearchItemData extends Record<string, unknown> {
  /** Normalized saved-search URL (utils/saved-searches/normalize-saved-search-url.ts). */
  url: string;
  label?: string;
  alertEnabled: boolean;
  createdAt: number;
  /** Id of the savedSearchFolder# this search is organized into; absent = "Unsorted". */
  folderId?: string;
  /** Manual sort position within its folder group; only meaningful in "Custom order" sort mode. */
  order?: number;
}

export interface SavedSearchFolderData extends Record<string, unknown> {
  name: string;
  /** Manual sort position among the user's other folders. */
  order: number;
  createdAt: number;
}

/**
 * The Watchlist digest (new-from-followed-companies / watched-roles sections), split out of
 * the combined emailAlerts subscription into its own independently-scheduled email — a
 * separate UserState item, not a field on EmailAlertsData, so it can carry its own
 * `alertSchedule` GSI entry (see email-alerts-store.ts) alongside the main one.
 *
 * Deliberately piggybacks on the main emailAlerts subscription rather than being a
 * standalone opt-in: it reuses that subscription's verified email/locale and needs no
 * separate consent flow (no pendingConfirmation state — "active" only exists while the
 * main subscription is also active; the write path enforces this, see subscribeWatchlistDigest
 * in server/alerts/email-alerts-service.ts). Existence of an active record *is* "separate
 * mode" — there is no explicit mode field to keep out of sync with the main subscription.
 */
export const WATCHLIST_DIGEST_ITEM_KEY = "watchlistDigestAlerts" as const;

export const WATCHLIST_DIGEST_STATUSES = ["active", "unsubscribed"] as const;
export type WatchlistDigestStatus = (typeof WATCHLIST_DIGEST_STATUSES)[number];

export interface WatchlistDigestAlertsData extends Record<string, unknown> {
  frequency: AlertFrequency;
  status: WatchlistDigestStatus;
  /** Epoch ms of the last successful watchlist-digest send; this email's own window lower bound. */
  lastSentAt: number | null;
  createdAt: number;
}

export function isAlertFrequency(value: unknown): value is AlertFrequency {
  return typeof value === "string" && (ALERT_FREQUENCIES as readonly string[]).includes(value);
}

export function isEmailAlertsStatus(value: unknown): value is EmailAlertsStatus {
  return typeof value === "string" && (EMAIL_ALERTS_STATUSES as readonly string[]).includes(value);
}

export function isWatchlistDigestStatus(value: unknown): value is WatchlistDigestStatus {
  return typeof value === "string" && (WATCHLIST_DIGEST_STATUSES as readonly string[]).includes(value);
}

export function savedSearchItemKey(id: string): UserStateSavedSearchKey {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error("[email-alerts] saved search id must be a non-empty string");
  }
  return `${SAVED_SEARCH_ITEM_KEY_PREFIX}${id}`;
}

export function savedSearchFolderItemKey(id: string): UserStateSavedSearchFolderKey {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error("[email-alerts] saved search folder id must be a non-empty string");
  }
  return `${SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX}${id}`;
}
